import { ArrowDown, ArrowUp, PackagePlus, X } from 'lucide-react';
import { useState } from 'react';
import { useWatch, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TagInput } from '@nucleus-commerce/admin-kit/forms';
import type { ChatAgent, ChatDiscountOptions } from '@nucleus-commerce/api-client/admin';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { ProductPicker } from '@/features/resource-pickers';

import { MAX_SUGGESTED_REPLIES, type TriggerForm } from '../schemas/trigger-schema';

interface TriggerActionRowProps {
  form: UseFormReturn<TriggerForm>;
  index: number;
  count: number;
  storeId: string;
  /** Members who can reply in the store's chat; null while loading. */
  agents: ChatAgent[] | null;
  discounts: ChatDiscountOptions | undefined;
  onMove: (to: number) => void;
  onRemove: () => void;
}

/** One step of the trigger: a message, a pause, a tag, a product card or a discount code. */
export function TriggerActionRow({
  form,
  index,
  count,
  storeId,
  agents,
  discounts,
  onMove,
  onRemove,
}: TriggerActionRowProps) {
  const { t } = useTranslation('chatSettings');
  const [picking, setPicking] = useState(false);
  const action = useWatch({ control: form.control, name: `actions.${index}` });
  const position = index + 1;

  return (
    <li className="space-y-3 rounded-md border p-3">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">{t(`triggers.actionTypes.${action.type}`)}</span>
        <span className="flex-1" />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('triggers.moveUp', { position })}
          disabled={index === 0}
          onClick={() => {
            onMove(index - 1);
          }}
        >
          <ArrowUp aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('triggers.moveDown', { position })}
          disabled={index === count - 1}
          onClick={() => {
            onMove(index + 1);
          }}
        >
          <ArrowDown aria-hidden />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={t('triggers.removeAction', { position })}
          disabled={count === 1}
          onClick={onRemove}
        >
          <X aria-hidden />
        </Button>
      </div>

      {action.type === 'message' ? (
        <>
          <FormField
            control={form.control}
            name={`actions.${index}.body`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('triggers.messageBody', { position })}</FormLabel>
                <FormControl>
                  <Textarea rows={3} {...field} />
                </FormControl>
                <FormDescription>{t('triggers.messageBodyHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField
              control={form.control}
              name={`actions.${index}.sendAs`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('triggers.sendAs')}</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="store">{t('triggers.sendAsStore')}</SelectItem>
                      <SelectItem value="alias">{t('triggers.sendAsAlias')}</SelectItem>
                      <SelectItem value="member">{t('triggers.sendAsMember')}</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />
            {action.sendAs === 'alias' ? (
              <FormField
                control={form.control}
                name={`actions.${index}.alias`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('triggers.alias')}</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {action.sendAs === 'member' ? (
              <FormField
                control={form.control}
                name={`actions.${index}.asUserId`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('triggers.member')}</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('triggers.memberPlaceholder')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(agents ?? [])
                          .filter((agent) => agent.canReply)
                          .map((agent) => (
                            <SelectItem key={agent.userId} value={agent.userId}>
                              {agent.chatDisplayName
                                ? t('triggers.memberShownAs', {
                                    name: agent.name,
                                    shown: agent.chatDisplayName,
                                  })
                                : agent.name}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
          </div>
          <FormField
            control={form.control}
            name={`actions.${index}.suggestedReplies`}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('triggers.suggestedReplies')}</FormLabel>
                <FormControl>
                  <TagInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    max={MAX_SUGGESTED_REPLIES}
                    maxLength={80}
                    placeholder={t('shortcuts.suggestedRepliesPlaceholder')}
                    removeLabel={(reply) => t('shortcuts.removeReply', { reply })}
                  />
                </FormControl>
                <FormDescription>{t('triggers.suggestedRepliesHint')}</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      ) : null}

      {action.type === 'wait' ? (
        <FormField
          control={form.control}
          name={`actions.${index}.seconds`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('triggers.waitFor')}</FormLabel>
              <div className="flex items-center gap-2">
                <FormControl>
                  <Input inputMode="numeric" className="w-28" {...field} />
                </FormControl>
                <span className="text-muted-foreground text-sm">{t('triggers.units.seconds')}</span>
              </div>
              <FormDescription>{t('triggers.waitHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      {action.type === 'tag' ? (
        <FormField
          control={form.control}
          name={`actions.${index}.tag`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('triggers.tag')}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormDescription>{t('triggers.tagHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      {action.type === 'product_card' ? (
        <FormField
          control={form.control}
          name={`actions.${index}.productId`}
          render={() => (
            <FormItem>
              <FormLabel>{t('triggers.product')}</FormLabel>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm">
                  {action.productId
                    ? action.productTitle ||
                      t('triggers.productById', { id: action.productId.slice(-6) })
                    : t('triggers.noProduct')}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPicking(true);
                  }}
                >
                  <PackagePlus aria-hidden />
                  {action.productId ? t('triggers.changeProduct') : t('triggers.chooseProduct')}
                </Button>
              </div>
              <FormMessage />
              <ProductPicker
                open={picking}
                onOpenChange={setPicking}
                scope={{ kind: 'store', storeId }}
                title={t('triggers.chooseProduct')}
                multiple={false}
                statuses={['published']}
                initialSelectedIds={action.productId ? [action.productId] : []}
                onConfirm={(selection) => {
                  const picked = selection.items[0];
                  form.setValue(`actions.${index}.productId`, selection.ids[0] ?? '', {
                    shouldDirty: true,
                    shouldValidate: true,
                  });
                  form.setValue(
                    `actions.${index}.productTitle`,
                    picked ? (picked.title ?? picked.product.title) : '',
                    { shouldDirty: true },
                  );
                }}
              />
            </FormItem>
          )}
        />
      ) : null}

      {action.type === 'discount' ? (
        <FormField
          control={form.control}
          name={`actions.${index}.discountId`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('triggers.discount')}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('triggers.discountPlaceholder')} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {(discounts?.items ?? []).map((discount) => (
                    <SelectItem key={discount.id} value={discount.id}>
                      {`${discount.title} · ${discount.summary}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>{t('triggers.discountHint')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}
    </li>
  );
}
